/**
 * Wire types and adapters for the public pay contract
 * (`contracts/invoicing-pay.openapi.yaml`, no auth — authorised by possession
 * of the `payToken`).
 *
 * ## What this file is for
 *
 * The seven pay operations answer with objects whose field names are not the
 * names the UI wants: the gateway returns `walletId`-style aliases and several
 * spellings of the same field (`AGENTS.md` "Field Name Variations"). Every
 * response is therefore mapped here, once, into a small view type the
 * components render — rather than being read field-by-field inside a
 * component, where a rename would be silent.
 *
 * ## The one rule
 *
 * A mapper returns `null` for a body it cannot read, and `null` means *the
 * service told us nothing*, never *the field is absent so use a default*
 * (`page-delivery-resources` gotcha 36, and the same contract the issuer client
 * in `../api.ts` holds). `pay-api.ts` turns that `null` into
 * `MALFORMED_RESPONSE`, so a service that answers `200 {}` produces an error
 * state, not an invoice with an empty amount and no issuer.
 *
 * Two consequences worth spelling out, because they are the ones a reviewer
 * looks for:
 *
 *   - A missing **amount** is never formatted as zero. Zero is displayed only
 *     when the service sent the base-unit string `"0"`.
 *   - A missing or malformed **address attestation** is carried through as
 *     `attestation: null`. It is not defaulted, not filled from the address,
 *     and it is the input the fail-closed gate in `attestation.ts` refuses on.
 */

import { isAssetDecimals, isBaseUnitString, type Money } from "../money";
import type { Asset } from "../types";

/* ---------------------------------------------------------------- helpers */

/**
 * Bounded display text: a trimmed string with control characters removed, cut
 * to `max`. Returns null for a non-string or an empty result — a caller that
 * needs the field then treats the body as unreadable rather than rendering
 * "undefined".
 */
/**
 * C0 and C1 control characters plus the Unicode bidi embedding, override and
 * isolate marks — the same set `src/i18n/support-reference.ts` strips, so no
 * value taken from the service can reorder or hide the text around it.
 */
const UNSAFE_CHARACTERS = /[\u0000-\u001F\u007F-\u009F\u200E\u200F\u202A-\u202E\u2066-\u2069]/g;

export function displayText(value: unknown, max = 160): string | null {
  if (typeof value !== "string") return null;
  const cleaned = value.replace(UNSAFE_CHARACTERS, "").trim();
  return cleaned ? cleaned.slice(0, max) : null;
}

/** The first of several wire spellings that yields usable text. */
function firstText(values: unknown[], max = 160): string | null {
  for (const value of values) {
    const text = displayText(value, max);
    if (text) return text;
  }
  return null;
}

/**
 * A wire `Money`. `{amount, asset:{symbol, decimals}}` is the contract shape;
 * a bare `{amount, symbol, decimals}` is accepted because the issuer routes
 * return that variant (see the field-alias table in `AGENTS.md`). Base units
 * stay a string end to end — nothing here is ever parsed into a `number`.
 */
export function mapMoney(value: unknown): Money | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as Record<string, unknown>;
  const asset = (raw.asset && typeof raw.asset === "object" ? raw.asset : raw) as Record<string, unknown>;
  const amount = isBaseUnitString(raw.amount) ? raw.amount : null;
  const symbol = displayText(asset.symbol, 16);
  const decimals = isAssetDecimals(asset.decimals) ? asset.decimals : null;
  if (amount === null || symbol === null || decimals === null) return null;
  return { amount, asset: { symbol, decimals } };
}

/** A network + asset pair the payer may send on. Unreadable ones are dropped. */
export interface PayNetworkView {
  /** Wire value sent back with the address request. Never displayed. */
  network: string;
  /** Human label. Falls back to the network id, never to a code alone. */
  label: string;
  asset: Asset;
}

function mapNetwork(value: unknown): PayNetworkView | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as Record<string, unknown>;
  const network = firstText([raw.network, raw.chain, raw.id], 64);
  if (!network) return null;
  const asset = (raw.asset && typeof raw.asset === "object" ? raw.asset : raw) as Record<string, unknown>;
  const symbol = displayText(asset.symbol, 16);
  const decimals = isAssetDecimals(asset.decimals) ? asset.decimals : null;
  if (symbol === null || decimals === null) return null;
  return {
    network,
    label: firstText([raw.label, raw.displayName, raw.name], 64) ?? network,
    asset: { symbol, decimals },
  };
}

/* ----------------------------------------------------------- declaration */

export interface PayDeclarationView {
  legalName: string;
  country: string;
  contactEmail: string;
  /**
   * True once the declaration is frozen (an address has been issued). The
   * service is the authority — `DECLARATION_FROZEN` on a later edit — and this
   * flag only decides whether the form is offered.
   */
  frozen: boolean;
}

export function mapDeclaration(value: unknown): PayDeclarationView | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as Record<string, unknown>;
  const legalName = firstText([raw.legalName, raw.legal_name], 160);
  const country = firstText([raw.country, raw.countryCode, raw.country_code], 8);
  const contactEmail = firstText([raw.contactEmail, raw.contact_email, raw.email], 254);
  if (!legalName || !country || !contactEmail) return null;
  return { legalName, country, contactEmail, frozen: raw.frozen === true || raw.locked === true };
}

/** What the payer submits. Sent verbatim; the service validates it. */
export interface PayDeclarationInput {
  legalName: string;
  country: string;
  contactEmail: string;
}

/* -------------------------------------------------------------- payment */

export interface PayPaymentView {
  /**
   * The wire status. Typed as a plain string rather than the closed
   * `PaymentStatus` union on purpose: a status this build has never heard of
   * must render as "Unknown" (`status.ts` `UNKNOWN_STATUS`), not become a
   * compile error someone resolves by inventing a case.
   */
  status: string;
  confirmations: number | null;
  requiredConfirmations: number | null;
  amountPaid: Money | null;
}

function nonNegativeInt(value: unknown): number | null {
  if (typeof value === "number" && Number.isInteger(value) && value >= 0) return value;
  if (typeof value === "string" && /^\d{1,7}$/.test(value)) return Number(value);
  return null;
}

export function mapPayment(value: unknown): PayPaymentView | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as Record<string, unknown>;
  const status = firstText([raw.status, raw.paymentStatus, raw.payment_status], 48);
  if (!status) return null;
  return {
    status,
    confirmations: nonNegativeInt(raw.confirmations ?? raw.confirmationsCount ?? raw.confirmations_count),
    requiredConfirmations: nonNegativeInt(
      raw.requiredConfirmations ?? raw.required_confirmations ?? raw.finalityThreshold ?? raw.finality_threshold,
    ),
    amountPaid: mapMoney(raw.amountPaid ?? raw.amount_paid ?? raw.paid),
  };
}

/* --------------------------------------------------------------- address */

export interface PayAddressAttestationWire {
  /** Identifies the signing key in the pinned trust root. Never a key itself. */
  keyId: string;
  /** Signature algorithm name, e.g. `Ed25519`. */
  algorithm: string;
  /** Base64 of the exact bytes the issuer signed. */
  payload: string;
  /** Base64 signature over `payload`. */
  signature: string;
}

export interface PayAddressView {
  address: string;
  network: string;
  /** Null when the service sent no attestation — the gate refuses on null. */
  attestation: PayAddressAttestationWire | null;
}

function mapAttestation(value: unknown): PayAddressAttestationWire | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as Record<string, unknown>;
  const keyId = firstText([raw.keyId, raw.key_id], 128);
  const algorithm = firstText([raw.algorithm, raw.alg], 32);
  const payload = firstText([raw.payload, raw.signedPayload, raw.signed_payload], 8192);
  const signature = firstText([raw.signature, raw.sig], 4096);
  if (!keyId || !algorithm || !payload || !signature) return null;
  return { keyId, algorithm, payload, signature };
}

export function mapAddress(value: unknown): PayAddressView | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as Record<string, unknown>;
  // The address itself: trimmed, no control characters, bounded. It is
  // rendered as text by React, so it can never become markup; the bound is so
  // a hostile 1 MB string cannot blow up the layout.
  const address = firstText([raw.address, raw.depositAddress, raw.deposit_address], 128);
  if (!address) return null;
  const network = firstText([raw.network, raw.chain], 64) ?? "";
  return { address, network, attestation: mapAttestation(raw.attestation) };
}

/* --------------------------------------------------------------- receipt */

export interface PayReceiptView {
  receiptId: string;
  /**
   * A same-origin download URL, or null when the service says a receipt is not
   * available yet. Anything absolute is dropped to null rather than linked:
   * this page must not hand the payer a link to a host it has not been told
   * about, and no third-party URL belongs on a checkout.
   */
  url: string | null;
  available: boolean;
}

function safeSameOriginPath(value: unknown): string | null {
  const text = displayText(value, 512);
  if (!text || !text.startsWith("/")) return null;
  if (text.startsWith("//") || text.includes("\\") || text.includes("\n") || text.includes("\r")) return null;
  return text;
}

export function mapReceipt(value: unknown): PayReceiptView | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as Record<string, unknown>;
  const receiptId = firstText([raw.receiptId, raw.receipt_id, raw.id], 128);
  if (!receiptId) return null;
  const url = safeSameOriginPath(raw.url ?? raw.downloadUrl ?? raw.download_url);
  return { receiptId, url, available: Boolean(url) && raw.available !== false };
}

/* ---------------------------------------------------------------- refund */

export interface PayRefundView {
  refundId: string;
  state: string;
  /** The address on file, once the payer has supplied one. */
  address: string | null;
  /** Where the one-time code went, masked by the service. Display only. */
  confirmationSentTo: string | null;
  confirmed: boolean;
}

export function mapRefund(value: unknown): PayRefundView | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as Record<string, unknown>;
  const refundId = firstText([raw.refundId, raw.refund_id, raw.id], 128);
  const state = firstText([raw.state, raw.status], 48);
  if (!refundId || !state) return null;
  return {
    refundId,
    state,
    address: firstText([raw.address, raw.refundAddress, raw.refund_address], 128),
    confirmationSentTo: firstText([raw.confirmationSentTo, raw.confirmation_sent_to, raw.maskedEmail], 254),
    confirmed: raw.confirmed === true || state === "confirmed",
  };
}

/* -------------------------------------------------------------- checkout */

/**
 * Everything `GET /v1/pay/{payToken}` gives the page. Each field has to be
 * readable for the body to count as read at all: a checkout without an issuer
 * legal name, an invoice number or an amount is not a checkout, and rendering
 * one would be exactly the "blank page that looks like data" the page spec
 * calls `blank_page`.
 */
export interface PayCheckoutView {
  issuerLegalName: string;
  invoiceNumber: string;
  invoiceAmount: Money;
  invoiceDueDate: string | null;
  networks: PayNetworkView[];
  declaration: PayDeclarationView | null;
  payment: PayPaymentView;
  /**
   * The refund awaiting an address, when the issuer has raised one. Null means
   * the checkout said nothing about a refund — which is not the same as "no
   * refund exists", and the panel therefore reads the service's own object
   * rather than inferring one from the payment status.
   */
  refund: PayRefundView | null;
}

export function mapCheckout(value: unknown): PayCheckoutView | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as Record<string, unknown>;

  const issuer = (raw.issuer && typeof raw.issuer === "object" ? raw.issuer : {}) as Record<string, unknown>;
  const issuerLegalName = firstText(
    [issuer.legalName, issuer.legal_name, raw.issuerLegalName, raw.issuer_legal_name],
    160,
  );

  // The invoice may be nested (`invoice`) or flattened onto the root; both
  // spellings appear across the contract family.
  const invoice = (raw.invoice && typeof raw.invoice === "object" ? raw.invoice : raw) as Record<string, unknown>;
  const invoiceNumber = firstText([invoice.number, invoice.invoiceNumber, invoice.invoice_number], 64);
  const invoiceAmount = mapMoney(invoice.amount ?? invoice.total ?? invoice.amountDue ?? invoice.amount_due);
  const invoiceDueDate = firstText([invoice.dueDate, invoice.due_date, invoice.due], 40);

  const payment = mapPayment(raw.payment ?? raw.paymentState ?? raw.payment_state ?? raw);

  if (!issuerLegalName || !invoiceNumber || !invoiceAmount || !payment) return null;

  const networksRaw = Array.isArray(raw.networks)
    ? raw.networks
    : Array.isArray(raw.acceptedNetworks)
      ? raw.acceptedNetworks
      : [];

  return {
    issuerLegalName,
    invoiceNumber,
    invoiceAmount,
    invoiceDueDate,
    networks: networksRaw.map(mapNetwork).filter((n): n is PayNetworkView => n !== null),
    declaration: mapDeclaration(raw.declaration),
    payment,
    refund: mapRefund(raw.refund ?? raw.refundRequest ?? raw.refund_request),
  };
}
